import { IconType } from "../Icon"
import { ILogLighting } from "../../Types"
import type { ApiUser } from "../../Types/Generated/LogbookApi/ApiRuntime"

// How much of an advice's wording a button shows (#1171). The buttons sit inline on
// the current-log row, where a handful of full labels -- "Alarme de profondeur
// désactivée" -- fill the row on their own. Every advice therefore declares both
// wordings up front and the row picks a format per button; see `Data.ts`.
export enum UserAdviceFormat {
  // The icon alone. The full wording survives as the button's accessible name.
  icon = "icon",
  // Icon + `shortTitle`: the advice's kind, with the variable parts (timers,
  // active/inactive state) dropped.
  short = "short",
  // Icon + `title`: the full wording, unchanged from before #1171.
  full = "full",
}

export enum UserAdviceLevel {
  notice = "notice",
  warning = "warning",
  alarm = "alarm",
};

// What job an advice does (#916). The advices used to be one flat list that all
// rendered into the current-log row, but they behave in three distinct ways, and
// a surface that cannot tell them apart has to show all of them: that is why the
// always-on monitors crowded the row alongside the alerts needing attention.
//
// Declared by the advice class rather than by the caller -- an advice is the only
// thing that knows what job it does, and every surface has to agree on the answer.
export enum UserAdviceRole {
  // Switched on and left running: an anchor or depth watch, and (once it lands)
  // navigation lights. Has an on/off state that outlives any single log, so it
  // belongs in the ship toolbar rather than the log row.
  monitor = "monitor",
  // Fired by a condition and demanding acknowledgement. Present only while the
  // alert is open.
  alert = "alert",
  // A prompt for something the crew may want to record right now, which opens a
  // dialog and is gone once handled.
  action = "action",
}

// Where a button is being rendered. Mirrors `UserAdviceLocation` (which lives with
// the advice classes in Views/UserAdvice) -- redeclared here so this presentational
// module stays independent of them, and kept string-valued so the two are
// interchangeable at the `adviceFormats` boundary.
export enum UserAdvicePlacement {
  log = "log",
  day = "day",
}

// The rendering width available to the advice row. Derived from the app's own
// mobile/small breakpoints by the caller rather than measured, so format selection
// stays a pure function (#1171).
export enum UserAdviceWidth {
  desktop = "desktop",
  mobile = "mobile",
  small = "small",
}

export interface UserAdviceProps {
  hideClose?: boolean;
  icon: IconType;
  navigationLights?: ILogLighting;
  level: UserAdviceLevel;
  title: string;
  // The short wording (#1171). Optional: an advice whose title is already a bare
  // noun has nothing to shorten and omits it, and the button falls back to `title`.
  shortTitle?: string;
  // Compact state chips painted even when the row has collapsed to icon-only.
  indicators?: string[];
  indicatorIcons?: { icon: IconType; label: string }[];
  type: string;
  // An action the button performs directly, turning it into a split button: the
  // button itself does this, and a caret opens the dialog (#916, @almute's
  // "Lösung 2"). Only the navigation-lights reminder sets it — everything the
  // dialog offers stays reachable through the caret, so nothing is lost by
  // short-cutting the common case.
  primaryAction?: UserAdvicePrimaryAction;
}

export interface UserAdvicePrimaryAction {
  // The accessible name and tooltip. The button paints the advice's own icon and
  // wording, so this says what the *click* will do, which is not the same thing.
  label: string;
  perform: () => void;
  // Whether this user may actually perform it. `false` collapses the split button
  // back to the plain "open the dialog" one -- a short-cut that 401s is worse than
  // no short-cut, and the dialog still offers everything. Defaults to permitted.
  //
  // Set either directly (fixtures, tests) or as the generated route predicate
  // (`<Api>.<method>(...).available`), which the button evaluates against the ship
  // context's `apiUser`. The advice cannot do that itself: `getInstances` has no
  // hooks, and `apiUser` only exists in React context.
  permitted?: boolean;
  available?: (user?: ApiUser) => boolean;
}
